import React, { useState, useEffect } from 'react';
import {
    MdPerson, MdLock, MdEdit, MdSave, MdCheckCircle,
    MdWarning, MdEmail, MdPhone, MdBadge
} from 'react-icons/md';

import { api, usersAPI } from 'services/api';

// --- YARDIMCI FONKSİYONLAR ---
const translateRole = (role) => {
    if (!role) return 'Kullanıcı';
    const roles = {
        'admin': 'Yönetici',
        'staff': 'Personel',
        'customer': 'Müşteri',
        'cashier': 'Kasa'
    };
    return roles[role.toLowerCase()] || role;
};

// --- SKELETON LOADER ---
const ProfileSkeleton = () => (
    <div className="flex flex-col md:flex-row gap-6 animate-pulse">
        {/* Sol Profil Kartı Skeleton */}
        <div className="w-full md:w-1/3 h-80 rounded-[20px] bg-gray-200 dark:bg-navy-700" />
        {/* Sağ Sekmeler ve Form Skeleton */}
        <div className="w-full md:w-2/3 h-96 rounded-[20px] bg-gray-200 dark:bg-navy-700" />
    </div>
);

const Profile = () => {
    // --- STATE TANIMLAMALARI (WORK-LOGIC BİREBİR) ---
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const [formData, setFormData] = useState({ name: '', surname: '', email: '', phone: '' });
    const [passwordData, setPasswordData] = useState({ new_password: '', new_password_confirmation: '' });

    // HorizonUI Extra Stateler
    const [activeTab, setActiveTab] = useState('info'); // 'info' | 'password'
    const [isInfoSubmitting, setIsInfoSubmitting] = useState(false);
    const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);

    // --- API VE VERİ ÇEKME ---
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await api.get('/me');
                if (response.success) {
                    setProfile(response.user);

                    // Telefon Numarası Formatlama (+90 ayıklama)
                    let cleanPhone = response.user.phone || '';
                    if (cleanPhone.startsWith('+90')) {
                        cleanPhone = cleanPhone.substring(3);
                    }

                    setFormData({
                        name: response.user.name || '',
                        surname: response.user.surname || '',
                        email: response.user.email || '',
                        phone: cleanPhone,
                    });
                }
            } catch (error) {
                console.error('Profil yüklenirken hata:', error);
                setMessage({ type: 'danger', text: 'Profil bilgileri yüklenemedi.' });
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, []);

    // --- İŞLEMLER (HANDLERS) ---
    const handleInfoChange = (e) => {
        const { name, value } = e.target;
        // Telefon için sadece rakam ve max 10 hane kontrolü
        if (name === 'phone') {
            const onlyNums = value.replace(/[^0-9]/g, '');
            if (onlyNums.length <= 10) {
                setFormData((prev) => ({ ...prev, [name]: onlyNums }));
            }
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData((prev) => ({ ...prev, [name]: value }));
    };

    const toggleEdit = () => {
        if (isEditing) {
            // İptal edilirse eski verilere dön
            let cleanPhone = profile.phone || '';
            if (cleanPhone.startsWith('+90')) cleanPhone = cleanPhone.substring(3);

            setFormData({
                name: profile.name || '',
                surname: profile.surname || '',
                email: profile.email || '',
                phone: cleanPhone,
            });
            setMessage({ type: '', text: '' });
        }
        setIsEditing(!isEditing);
    };

    // Bilgi Güncelleme (Telefon +90 ekleme mantığı dahil)
    const handleInfoSubmit = async (e) => {
        e.preventDefault();
        if (formData.phone && (formData.phone.length !== 10 || !formData.phone.startsWith('5'))) {
            setMessage({ type: 'danger', text: 'Telefon numarası 5 ile başlamalı ve 10 haneli olmalıdır. (Örn: 5425788842)' });
            return;
        }

        setIsInfoSubmitting(true);
        setMessage({ type: '', text: '' });

        try {
            const payload = {
                ...formData,
                phone: formData.phone ? `+90${formData.phone}` : ''
            };

            const response = await usersAPI.update(profile.id, payload);
            if (response.success || response.user) {
                setProfile(response.user || payload); // API yanıtına göre güvene alma
                setIsEditing(false);
                setMessage({ type: 'success', text: 'Profil bilgileriniz başarıyla güncellendi.' });
                setTimeout(() => setMessage({ type: '', text: '' }), 4000);
            }
        } catch (error) {
            console.error('Profil güncellenirken hata:', error);
            const errorMsg = error.response?.data?.errors
                ? Object.values(error.response.data.errors).join(', ')
                : 'Bilgileriniz güncellenirken bir hata oluştu.';
            setMessage({ type: 'danger', text: errorMsg });
        } finally {
            setIsInfoSubmitting(false);
        }
    };

    // Şifre Güncelleme
    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (passwordData.new_password !== passwordData.new_password_confirmation) {
            setMessage({ type: 'danger', text: 'Girdiğiniz yeni şifreler uyuşmuyor.' });
            return;
        }
        if (passwordData.new_password.length < 6) {
            setMessage({ type: 'danger', text: 'Şifreniz güvenliğiniz için en az 6 karakter olmalıdır.' });
            return;
        }

        setIsPasswordSubmitting(true);
        setMessage({ type: '', text: '' });

        try {
            const response = await usersAPI.update(profile.id, {
                password: passwordData.new_password,
            });
            if (response.success || response.message) {
                setPasswordData({ new_password: '', new_password_confirmation: '' });
                setMessage({ type: 'success', text: 'Şifreniz başarıyla değiştirildi.' });
                setTimeout(() => setMessage({ type: '', text: '' }), 4000);
            }
        } catch (error) {
            console.error('Şifre güncellenirken hata:', error);
            setMessage({ type: 'danger', text: 'Şifre değiştirme işlemi başarısız oldu.' });
        } finally {
            setIsPasswordSubmitting(false);
        }
    };


    // --- RENDER ---
    if (loading) {
        return (
            <div className="mt-5 w-full pb-20">
                <ProfileSkeleton />
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="mt-5 p-4 rounded-xl bg-red-50 text-red-600 border border-red-200 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400 font-bold flex items-center gap-2">
                <MdWarning className="text-xl" /> Profil bilgileri yüklenemedi.
            </div>
        );
    }

    return (
        <div className="mt-5 flex w-full flex-col gap-6 pb-20 animate-fadeIn">

            {/* GENEL BİLDİRİM ALANI */}
            {message.text && (
                <div className={`flex items-center gap-3 p-4 rounded-xl border ${message.type === 'danger' ? 'bg-red-50 border-red-200 text-red-600 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400' : 'bg-green-50 border-green-200 text-green-600 dark:bg-green-500/10 dark:border-green-500/20 dark:text-green-400'}`}>
                    {message.type === 'danger' ? <MdWarning className="text-xl flex-none" /> : <MdCheckCircle className="text-xl flex-none" />}
                    <span className="text-sm font-bold">{message.text}</span>
                </div>
            )}

            <div className="flex flex-col md:flex-row gap-6">

                {/* 1. SOL: PROFİL KARTI (BRAND GRADIENT) */}
                <div className="w-full md:w-1/3 flex flex-col h-full">
                    <div className="relative flex flex-col items-center justify-center p-8 rounded-[20px] bg-gradient-to-br from-brand-400 to-brand-600 dark:from-brand-500 dark:to-brand-700 shadow-xl shadow-brand-500/20 text-white overflow-hidden">
                        {/* Dekoratif İkon */}
                        <div className="absolute -top-6 -right-6 opacity-10">
                            <MdBadge className="text-[150px]" />
                        </div>

                        <div className="relative z-10 flex flex-col items-center">
                            <div className="flex h-28 w-28 items-center justify-center rounded-full bg-white/20 backdrop-blur-md border-2 border-white/30 text-4xl font-black shadow-inner mb-4">
                                {(profile.name?.charAt(0) || '')}{(profile.surname?.charAt(0) || '')}
                            </div>
                            <h3 className="text-2xl font-black mb-1 text-center">{profile.name} {profile.surname}</h3>
                            <span className="px-4 py-1 mt-2 rounded-full bg-white/20 text-white text-xs font-bold uppercase tracking-widest backdrop-blur-sm border border-white/10">
                {translateRole(profile.role)}
              </span>
                        </div>
                    </div>
                </div>

                {/* 2. SAĞ: SEKME VE FORMLAR */}
                <div className="w-full md:w-2/3 flex flex-col bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10 overflow-hidden">

                    {/* Sekmeler (Tabs) */}
                    <div className="flex items-center border-b border-gray-100 dark:border-white/10 bg-gray-50/50 dark:bg-navy-900/20">
                        <button
                            onClick={() => setActiveTab('info')}
                            className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-bold transition-all ${activeTab === 'info' ? 'border-b-2 border-brand-500 text-brand-500 bg-white dark:bg-navy-800' : 'text-gray-500 hover:text-navy-700 dark:hover:text-white'}`}
                        >
                            <MdPerson className="text-lg" /> Profil Bilgileri
                        </button>
                        <button
                            onClick={() => setActiveTab('password')}
                            className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-bold transition-all ${activeTab === 'password' ? 'border-b-2 border-brand-500 text-brand-500 bg-white dark:bg-navy-800' : 'text-gray-500 hover:text-navy-700 dark:hover:text-white'}`}
                        >
                            <MdLock className="text-lg" /> Şifre Güvenliği
                        </button>
                    </div>

                    <div className="p-6">
                        {/* --- BİLGİ SEKMESİ --- */}
                        {activeTab === 'info' && (
                            <form onSubmit={handleInfoSubmit} className="flex flex-col gap-5">
                                <div className="flex justify-between items-center mb-2">
                                    <h5 className="text-lg font-bold text-navy-700 dark:text-white">Kişisel Detaylar</h5>
                                    <button
                                        type="button"
                                        onClick={toggleEdit}
                                        className={`flex items-center gap-1 px-4 py-1.5 rounded-lg text-xs font-bold transition-colors ${isEditing ? 'bg-gray-100 text-gray-600 dark:bg-navy-700 dark:text-gray-300' : 'bg-brand-50 text-brand-500 hover:bg-brand-500 hover:text-white dark:bg-brand-400/10 dark:text-brand-400 dark:hover:bg-brand-400 dark:hover:text-navy-900'}`}
                                    >
                                        <MdEdit className="text-sm" /> {isEditing ? 'Düzenlemeyi İptal Et' : 'Bilgileri Düzenle'}
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="text-xs font-bold text-gray-500 dark:text-gray-400 ml-1 mb-1 block">Adınız</label>
                                        <div className="relative">
                                            <MdPerson className={`absolute left-3 top-1/2 -translate-y-1/2 text-lg ${isEditing ? 'text-brand-500' : 'text-gray-400'}`} />
                                            <input
                                                type="text" name="name" value={formData.name} onChange={handleInfoChange} readOnly={!isEditing} required
                                                className={`w-full h-11 pl-10 pr-3 rounded-xl border text-sm font-bold outline-none transition-all ${isEditing ? 'border-gray-200 bg-white text-navy-700 focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white' : 'border-transparent bg-gray-50 text-gray-600 dark:bg-navy-900/50 dark:text-gray-400 cursor-default'}`}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold text-gray-500 dark:text-gray-400 ml-1 mb-1 block">Soyadınız</label>
                                        <div className="relative">
                                            <MdPerson className={`absolute left-3 top-1/2 -translate-y-1/2 text-lg ${isEditing ? 'text-brand-500' : 'text-gray-400'}`} />
                                            <input
                                                type="text" name="surname" value={formData.surname} onChange={handleInfoChange} readOnly={!isEditing} required
                                                className={`w-full h-11 pl-10 pr-3 rounded-xl border text-sm font-bold outline-none transition-all ${isEditing ? 'border-gray-200 bg-white text-navy-700 focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white' : 'border-transparent bg-gray-50 text-gray-600 dark:bg-navy-900/50 dark:text-gray-400 cursor-default'}`}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-xs font-bold text-gray-500 dark:text-gray-400 ml-1 mb-1 block">E-posta Adresiniz</label>
                                    <div className="relative">
                                        <MdEmail className={`absolute left-3 top-1/2 -translate-y-1/2 text-lg ${isEditing ? 'text-brand-500' : 'text-gray-400'}`} />
                                        <input
                                            type="email" name="email" value={formData.email} onChange={handleInfoChange} readOnly={!isEditing} required
                                            className={`w-full h-11 pl-10 pr-3 rounded-xl border text-sm font-bold outline-none transition-all ${isEditing ? 'border-gray-200 bg-white text-navy-700 focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white' : 'border-transparent bg-gray-50 text-gray-600 dark:bg-navy-900/50 dark:text-gray-400 cursor-default'}`}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="text-xs font-bold text-gray-500 dark:text-gray-400 ml-1 mb-1 block">Telefon Numaranız</label>
                                    <div className="flex h-11 rounded-xl overflow-hidden shadow-sm">
                                        {/* Sabit +90 Alanı */}
                                        <div className="flex items-center justify-center px-4 bg-gray-100 dark:bg-navy-700 border border-gray-200 border-r-0 dark:border-white/10">
                                            <span className="text-sm font-black text-gray-600 dark:text-gray-300 flex items-center gap-1"><MdPhone/> +90</span>
                                        </div>
                                        {/* Telefon Inputu */}
                                        <input
                                            type="tel" name="phone" value={formData.phone} onChange={handleInfoChange} readOnly={!isEditing} placeholder="5XXXXXXXXX" maxLength="10"
                                            className={`flex-1 w-full h-full px-3 border text-sm font-bold outline-none transition-all ${isEditing ? 'border-gray-200 bg-white text-navy-700 focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white' : 'border-transparent bg-gray-50 text-gray-600 dark:bg-navy-900/50 dark:text-gray-400 cursor-default'}`}
                                        />
                                    </div>
                                    {isEditing && <span className="text-[10px] text-gray-400 ml-1 mt-1 block">Telefon numaranızı 5 ile başlayacak şekilde 10 haneli olarak giriniz.</span>}
                                </div>

                                {isEditing && (
                                    <div className="flex justify-end pt-4 mt-2 border-t border-gray-100 dark:border-white/10">
                                        <button type="submit" disabled={isInfoSubmitting} className="flex items-center justify-center gap-2 h-11 px-8 rounded-xl bg-brand-500 text-white text-sm font-bold shadow-lg shadow-brand-500/30 hover:bg-brand-600 disabled:opacity-50 transition-all active:scale-95">
                                            {isInfoSubmitting ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><MdSave className="text-lg"/> Değişiklikleri Kaydet</>}
                                        </button>
                                    </div>
                                )}
                            </form>
                        )}

                        {/* --- ŞİFRE SEKMESİ --- */}
                        {activeTab === 'password' && (
                            <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-5">
                                <div className="mb-2">
                                    <h5 className="text-lg font-bold text-navy-700 dark:text-white">Şifre Değiştir</h5>
                                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mt-1">Hesap güvenliğiniz için şifrenizin en az 6 karakter olmasına özen gösterin.</p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="text-xs font-bold text-gray-500 dark:text-gray-400 ml-1 mb-1 block">Yeni Şifre</label>
                                        <div className="relative">
                                            <MdLock className="absolute left-3 top-1/2 -translate-y-1/2 text-lg text-amber-500" />
                                            <input
                                                type="password" name="new_password" value={passwordData.new_password} onChange={handlePasswordChange} placeholder="••••••" required minLength={6}
                                                className="w-full h-11 pl-10 pr-3 rounded-xl border border-gray-200 bg-white text-sm font-bold text-navy-700 outline-none focus:border-amber-500 dark:border-white/10 dark:bg-navy-900 dark:text-white transition-all shadow-sm"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold text-gray-500 dark:text-gray-400 ml-1 mb-1 block">Yeni Şifre (Tekrar)</label>
                                        <div className="relative">
                                            <MdLock className="absolute left-3 top-1/2 -translate-y-1/2 text-lg text-amber-500" />
                                            <input
                                                type="password" name="new_password_confirmation" value={passwordData.new_password_confirmation} onChange={handlePasswordChange} placeholder="••••••" required minLength={6}
                                                className="w-full h-11 pl-10 pr-3 rounded-xl border border-gray-200 bg-white text-sm font-bold text-navy-700 outline-none focus:border-amber-500 dark:border-white/10 dark:bg-navy-900 dark:text-white transition-all shadow-sm"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end pt-4 mt-2 border-t border-gray-100 dark:border-white/10">
                                    <button type="submit" disabled={isPasswordSubmitting || !passwordData.new_password} className="flex items-center justify-center gap-2 h-11 px-8 rounded-xl bg-amber-500 text-white text-sm font-bold shadow-lg shadow-amber-500/30 hover:bg-amber-600 disabled:opacity-50 transition-all active:scale-95">
                                        {isPasswordSubmitting ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><MdLock className="text-lg"/> Şifreyi Güncelle</>}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
};

export default Profile;